'use client'

import { ReactNode, useState, useMemo } from 'react'
import { cn } from '@heroui/react'
import { Button } from './button'

// Grid column configuration
export interface GridColumns {
  base?: number
  sm?: number
  md?: number
  lg?: number
  xl?: number
  '2xl'?: number
}

// Grid gap sizes
export type GridGap = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl'

// Grid alignment options
export type GridAlign = 'start' | 'center' | 'end' | 'stretch'

// Aspect ratio policy
export type AspectPolicy = 'normalize-by-kind' | 'respect-prop'

// Pagination configuration
export interface PaginationConfig {
  enabled: boolean
  pageSize: number
  loadMoreText?: string
  loadingText?: string
  showTotal?: boolean
  onLoadMore?: (currentPage: number, nextPage: number) => Promise<void> | void
}

// Empty state configuration
export interface EmptyStateConfig {
  icon?: ReactNode
  title?: string
  description?: string
  action?: ReactNode
}

// Main Grid props
export interface GridProps<T> {
  /** Array of items to render */
  items: T[]
  /** Function to render each item */
  renderItem: (item: T, index: number) => ReactNode
  /** Responsive column configuration */
  columns?: GridColumns
  /** Gap between grid items */
  gap?: GridGap
  /** Grid alignment */
  align?: GridAlign
  /** How to handle aspect ratios */
  aspectPolicy?: AspectPolicy
  /** Pagination configuration */
  paginate?: PaginationConfig
  /** Empty state when no items */
  emptyState?: EmptyStateConfig
  /** Additional CSS classes */
  className?: string
  /** Loading state */
  loading?: boolean
  /** Error state */
  error?: string | null
  /** Grid container props */
  containerProps?: React.HTMLAttributes<HTMLDivElement>
  /** Test ID */
  'data-testid'?: string
}

// Column class mappings for Tailwind
const getColumnClasses = (columns: GridColumns): string => {
  const classes: string[] = []
  
  if (columns.base) classes.push(`grid-cols-${columns.base}`)
  if (columns.sm) classes.push(`sm:grid-cols-${columns.sm}`)
  if (columns.md) classes.push(`md:grid-cols-${columns.md}`)
  if (columns.lg) classes.push(`lg:grid-cols-${columns.lg}`)
  if (columns.xl) classes.push(`xl:grid-cols-${columns.xl}`)
  if (columns['2xl']) classes.push(`2xl:grid-cols-${columns['2xl']}`)
  
  return classes.join(' ')
}

// Gap class mappings
const gapClasses: Record<GridGap, string> = {
  none: 'gap-0',
  xs: 'gap-1',
  sm: 'gap-2', 
  md: 'gap-4',
  lg: 'gap-6',
  xl: 'gap-8'
}

// Alignment class mappings
const alignClasses: Record<GridAlign, string> = {
  start: 'items-start',
  center: 'items-center', 
  end: 'items-end',
  stretch: 'items-stretch'
}

// Default empty state
const DefaultEmptyState = ({ config }: { config?: EmptyStateConfig }) => (
  <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
    <div className="mb-4 text-gray-300">
      {config?.icon || (
        <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2 2v-5m16 0h-5.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H1" />
        </svg>
      )}
    </div>
    <h3 className="text-lg font-medium text-gray-900 mb-2">
      {config?.title || 'No items found'}
    </h3>
    <p className="text-gray-500 mb-4 max-w-sm">
      {config?.description || 'There are no items to display at this time.'}
    </p>
    {config?.action}
  </div>
)

// Error state component
const ErrorState = ({ error }: { error: string }) => (
  <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
    <div className="mb-4 text-red-300">
      <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.498 0L4.316 16.5c-.77.833.192 2.5 1.732 2.5z" />
      </svg>
    </div>
    <h3 className="text-lg font-medium text-gray-900 mb-2">
      Something went wrong
    </h3>
    <p className="text-gray-500 max-w-sm">
      {error}
    </p>
  </div>
)

// Loading state component
const LoadingState = ({ columns }: { columns: GridColumns }) => {
  const skeletonCount = columns.lg || columns.md || columns.sm || columns.base || 4
  
  return (
    <>
      {Array.from({ length: skeletonCount }).map((_, index) => (
        <div key={index} className="animate-pulse">
          <div className="bg-gray-200 rounded-lg aspect-square mb-3" />
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded w-3/4" />
            <div className="h-3 bg-gray-200 rounded w-1/2" />
          </div>
        </div>
      ))}
    </>
  )
}

// Main Grid component
export function Grid<T>({
  items,
  renderItem,
  columns = { base: 1, sm: 2, md: 3, lg: 4 },
  gap = 'md',
  align = 'stretch',
  aspectPolicy = 'respect-prop',
  paginate,
  emptyState,
  className,
  loading = false,
  error = null,
  containerProps,
  'data-testid': testId,
  ...props
}: GridProps<T>) {
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  
  // Calculate displayed items based on pagination
  const displayedItems = useMemo(() => {
    if (!paginate?.enabled) return items
    
    const itemsToShow = currentPage * paginate.pageSize
    return items.slice(0, itemsToShow)
  }, [items, currentPage, paginate])
  
  // Calculate if there are more items to load
  const hasMoreItems = useMemo(() => {
    if (!paginate?.enabled) return false
    return displayedItems.length < items.length
  }, [displayedItems, items, paginate])
  
  // Handle load more
  const handleLoadMore = async () => {
    if (!paginate?.enabled || isLoadingMore) return
    
    setIsLoadingMore(true)
    
    try {
      if (paginate.onLoadMore) {
        await paginate.onLoadMore(currentPage, currentPage + 1)
      }
      setCurrentPage(prev => prev + 1)
    } catch (err) {
      console.error('Failed to load more items:', err)
    } finally {
      setIsLoadingMore(false)
    }
  }
  
  // Generate grid classes
  const gridClasses = cn(
    'grid',
    getColumnClasses(columns),
    gapClasses[gap],
    alignClasses[align],
    // Aspect policy data attribute for CSS targeting
    aspectPolicy === 'normalize-by-kind' && 'grid-aspect-normalize',
    className
  )
  
  // Handle error state
  if (error) {
    return (
      <div className={gridClasses} data-testid={testId} {...containerProps}>
        <ErrorState error={error} />
      </div>
    )
  }
  
  // Handle loading state
  if (loading && displayedItems.length === 0) {
    return (
      <div className={gridClasses} data-testid={testId} {...containerProps}>
        <LoadingState columns={columns} />
      </div>
    )
  }
  
  // Handle empty state
  if (displayedItems.length === 0 && !loading) {
    return (
      <div className={gridClasses} data-testid={testId} {...containerProps}>
        <DefaultEmptyState config={emptyState} />
      </div>
    )
  }
  
  return (
    <div className="space-y-6" data-testid={testId}>
      {/* Main Grid */}
      <div className={gridClasses} data-aspect-policy={aspectPolicy} {...containerProps}>
        {displayedItems.map(renderItem)}
      </div>
      
      {/* Pagination Controls */}
      {paginate?.enabled && (
        <div className="flex flex-col items-center space-y-4">
          {/* Progress indicator */}
          {paginate.showTotal && (
            <div className="text-sm text-gray-500">
              Showing {displayedItems.length} of {items.length} items
              {displayedItems.length < items.length && (
                <span className="ml-2">
                  ({items.length - displayedItems.length} more available)
                </span>
              )}
            </div>
          )}
          
          {/* Load More Button */}
          {hasMoreItems && (
            <Button
              variant="outline"
              onPress={handleLoadMore}
              isLoading={isLoadingMore}
              isDisabled={isLoadingMore}
              size="lg"
              className="px-8"
            >
              {isLoadingMore
                ? paginate.loadingText || 'Loading...'
                : paginate.loadMoreText || 'Load More'}
            </Button>
          )}
          
          {/* End of results indicator */}
          {!hasMoreItems && displayedItems.length > 0 && displayedItems.length === items.length && (
            <div className="text-sm text-gray-400 text-center py-4">
              <div className="flex items-center justify-center space-x-2">
                <div className="h-px bg-gray-200 flex-1" />
                <span>You've reached the end</span>
                <div className="h-px bg-gray-200 flex-1" />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Utility hook for managing grid state
export function useGridState<T>(initialItems: T[] = []) {
  const [items, setItems] = useState<T[]>(initialItems)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  const addItems = (newItems: T[]) => {
    setItems(prev => [...prev, ...newItems])
  }
  
  const setItemsWithLoading = async (loadFn: () => Promise<T[]>) => {
    setLoading(true)
    setError(null)
    
    try {
      const newItems = await loadFn()
      setItems(newItems)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }
  
  const loadMore = async (loadFn: () => Promise<T[]>) => {
    setLoading(true)
    setError(null)
    
    try {
      const newItems = await loadFn()
      addItems(newItems)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more items')
    } finally {
      setLoading(false)
    }
  }
  
  return {
    items,
    loading,
    error,
    setItems,
    addItems,
    setItemsWithLoading,
    loadMore,
    clearError: () => setError(null)
  }
}

// Predefined column configurations
export const gridPresets = {
  single: { base: 1 },
  double: { base: 1, sm: 2 },
  triple: { base: 1, sm: 2, md: 3 },
  quad: { base: 1, sm: 2, md: 3, lg: 4 },
  responsive: { base: 1, sm: 2, md: 3, lg: 4, xl: 5, '2xl': 6 },
  dense: { base: 2, sm: 3, md: 4, lg: 5, xl: 6, '2xl': 8 }
} as const

export default Grid